/**
 * F37 (Wave 2D) — curated badge-icon picker choices + the icon-code resolver
 * lookup tables (D13). Kept in a non-component module so the `BadgeIcon` /
 * `BadgeIconPicker` component files stay fast-refresh-clean
 * (react-refresh/only-export-components).
 *
 * Every `BADGE_ICON_CHOICES` entry is a REAL design-token icon code that
 * `BadgeIcon` resolves directly, plus the four Wave-1-seeded aliases
 * (`trending-up`, `handshake`, `users`, `zap`) so editing an already-seeded
 * badge round-trips without forcing a re-pick.
 */
import {
  STATUS_REGISTRY,
  UI_REGISTRY,
  type StatusIconName,
  type UIIconName,
} from '@bravemove/design-tokens/icons';

export const UI_ICON_NAME_SET = new Set<string>(Object.keys(UI_REGISTRY));
export const STATUS_ICON_NAME_SET = new Set<string>(Object.keys(STATUS_REGISTRY));

export type BadgeIconAlias =
  | { kind: 'ui'; name: UIIconName }
  | { kind: 'status'; name: StatusIconName };

/**
 * Wave-1 seeded codes that aren't a verbatim registry key → nearest real icon.
 * Keeps seeded badges (`most_improved`, `team_player`, `quick_learner`,
 * `great_sportsmanship`) rendering a sensible glyph until an admin re-picks.
 */
export const SEEDED_ICON_ALIAS: Record<string, BadgeIconAlias> = {
  'trending-up': { kind: 'ui', name: 'up' },
  trending_up: { kind: 'ui', name: 'up' },
  handshake: { kind: 'ui', name: 'team' },
  users: { kind: 'ui', name: 'team' },
  zap: { kind: 'status', name: 'bolt' },
};

export interface BadgeIconChoice {
  /** The value stored in `badge_catalog.icon_code`. */
  readonly code: string;
  /** A short human label for the picker grid. */
  readonly label: string;
}

export const BADGE_ICON_CHOICES: readonly BadgeIconChoice[] = [
  // Award / recognition
  { code: 'star', label: 'Star' },
  { code: 'trophy', label: 'Trophy' },
  { code: 'cup', label: 'Cup' },
  { code: 'heart', label: 'Heart' },
  { code: 'verified', label: 'Verified' },
  { code: 'shield', label: 'Shield' },
  // Progress / skill
  { code: 'up', label: 'Trending up' },
  { code: 'bolt', label: 'Lightning' },
  { code: 'flame', label: 'Flame' },
  { code: 'pulse', label: 'BraveMove' },
  // Community / sport
  { code: 'team', label: 'Team' },
  { code: 'whistle', label: 'Whistle' },
  { code: 'flag', label: 'Flag' },
  { code: 'user', label: 'Player' },
  { code: 'check', label: 'Check' },
  { code: 'bookmark', label: 'Bookmark' },
  // Seeded aliases (legacy codes — kept so existing badges round-trip)
  { code: 'trending-up', label: 'Trending up (legacy)' },
  { code: 'handshake', label: 'Handshake (legacy)' },
  { code: 'users', label: 'Players (legacy)' },
  { code: 'zap', label: 'Zap (legacy)' },
];
